import { oc } from "@orpc/contract";
import { z } from "zod";
import { InvoiceStatusSchema } from "./ar-invoices.contract";
import { ArAgingViewModeSchema } from "./ar-custom-aging.contract";

/**
 * AR Saved Reports RPC Contract
 *
 * Provides CRUD endpoints for persisting named report filter configurations.
 * Saved reports are per-user (not shared across an organization) and store
 * the complete filter state for the custom aging report.
 */

export const ArSavedReportFilterConfigSchema = z
  .object({
    minDays: z.number().int().min(0),
    maxDays: z.number().int().min(0).optional(),
    /** When true, the saved report restores with the "not yet due" toggle
     *  enabled. Only meaningful for basis="days_overdue". */
    currentOnly: z.boolean().optional(),
    basis: z.enum([
      "days_overdue",
      "days_since_invoice",
      "days_since_shipment",
    ]),
    customerIds: z.array(z.string().uuid()).optional(),
    status: z.array(InvoiceStatusSchema).optional(),
    /**
     * Persisted "last statement sent to customer" filter — minimum/maximum
     * days since the last statement email, and whether never-sent customers
     * should be included. All optional; omit to skip that side of the filter.
     */
    lastSentMinDays: z.number().int().min(0).optional(),
    lastSentMaxDays: z.number().int().min(0).optional(),
    includeNeverSent: z.boolean().optional(),
    sortBy: z.enum(["customerName", "totalAmount", "invoiceCount"]).optional(),
    sortDirection: z.enum(["asc", "desc"]).optional(),
    viewMode: ArAgingViewModeSchema.optional(),
  })
  .refine(
    (data) => data.maxDays === undefined || data.maxDays >= data.minDays,
    {
      message: "maxDays must be >= minDays",
      path: ["maxDays"],
    },
  )
  .refine(
    (data) =>
      data.lastSentMaxDays === undefined ||
      data.lastSentMinDays === undefined ||
      data.lastSentMaxDays >= data.lastSentMinDays,
    {
      message: "lastSentMaxDays must be >= lastSentMinDays",
      path: ["lastSentMaxDays"],
    },
  );

export const ArSavedReportSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  filterConfig: ArSavedReportFilterConfigSchema,
  isDefault: z.boolean(),
  lastOpenedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const CreateArSavedReportInputSchema = z.object({
  name: z.string().min(1).max(255),
  filterConfig: ArSavedReportFilterConfigSchema,
  isDefault: z.boolean().optional(),
});

export const UpdateArSavedReportInputSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255).optional(),
  filterConfig: ArSavedReportFilterConfigSchema.optional(),
  isDefault: z.boolean().optional(),
});

export const DeleteArSavedReportInputSchema = z.object({
  id: z.string().uuid(),
});

export const GetArSavedReportInputSchema = z.object({
  id: z.string().uuid(),
});

export const arSavedReportsContracts = {
  create: oc
    .input(CreateArSavedReportInputSchema)
    .output(ArSavedReportSchema)
    .route({ method: "POST" }),
  list: oc
    .output(z.object({ reports: z.array(ArSavedReportSchema) }))
    .route({ method: "GET" }),
  get: oc
    .input(GetArSavedReportInputSchema)
    .output(ArSavedReportSchema)
    .route({ method: "GET" }),
  update: oc
    .input(UpdateArSavedReportInputSchema)
    .output(ArSavedReportSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeleteArSavedReportInputSchema)
    .output(z.object({ success: z.boolean() }))
    .route({ method: "POST" }),
} as const;

// Type exports
export type ArSavedReportFilterConfig = z.infer<
  typeof ArSavedReportFilterConfigSchema
>;
export type ArSavedReport = z.infer<typeof ArSavedReportSchema>;
export type CreateArSavedReportInput = z.infer<
  typeof CreateArSavedReportInputSchema
>;
export type UpdateArSavedReportInput = z.infer<
  typeof UpdateArSavedReportInputSchema
>;
export type DeleteArSavedReportInput = z.infer<
  typeof DeleteArSavedReportInputSchema
>;
export type GetArSavedReportInput = z.infer<typeof GetArSavedReportInputSchema>;
